// src/components/generic/StickyBox/StickyBox.tsx

import React from "react";
import { Box, useMantineTheme } from "@mantine/core";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

export interface StickyBoxProps {
  /**
   * Top offset in pixels on desktop (default: 10).
   */
  stickyOffset?: number;

  /**
   * Top offset in pixels on mobile (default: 70).
   * Allows accounting for a fixed mobile header/nav bar.
   */
  mobileStickyOffset?: number;

  /** Z-index for the sticky element (default: 10) */
  stickyZIndex?: number;

  /**
   * Background color. Accepts any Mantine color string or CSS value.
   * Defaults to theme.colors.neutral[0].
   */
  bg?: string;

  /** Additional className */
  className?: string;

  /** Additional styles (merged with sticky styles, applied last) */
  style?: React.CSSProperties;

  children: React.ReactNode;
}

export const StickyBox: React.FC<StickyBoxProps> = ({
  stickyOffset = 10,
  mobileStickyOffset = 70,
  stickyZIndex = 10,
  bg,
  className,
  style,
  children,
}) => {
  const theme = useMantineTheme();
  const { isMobile } = useAppEnvironment();

  const backgroundColor = bg ?? theme.colors.neutral[0];
  const top = isMobile ? mobileStickyOffset : stickyOffset;

  return (
    <Box
      className={className}
      style={{
        position: "sticky",
        top,
        zIndex: stickyZIndex,
        backgroundColor,
        ...style,
      }}
    >
      {children}
    </Box>
  );
};